import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as PO from '@radix-ui/react-popover';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Bell,
  CheckCheck,
  Eye,
  Film,
  MessageCircle,
  PartyPopper,
  Trash2,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import type { InboxItem } from '@teaplus/shared';
import { ProfileAvatar } from '../profile/avatar';
import { errorMessage } from '@/lib/api';
import { ago } from '@/lib/ago';
import { clearInbox, markInboxRead, useInbox } from '@/lib/inbox';
import { usePartyEnabled } from '@/lib/party';
import { chatPath, useCanSocialise } from '@/lib/social';
import { cn } from '@/lib/cn';

const titlePath = (type: string, id: string) =>
  type === 'movie' ? `/movie/${id}` : `/series/${id}`;

const episodeOf = (m: { season: number | null; episode: number | null }) =>
  m.season != null && m.episode != null ? `S${m.season} E${m.episode}` : null;

const bold = (t: string) => <span className="font-semibold">{t}</span>;

interface Described {
  text: ReactNode;
  detail?: string | null;
  icon: ReactNode;
  /** The icon's colour, one per kind, so they're told apart at a glance. */
  tone: string;
  poster?: string | null;
  /** Where clicking it goes, if anywhere. */
  to: string | null;
}

/** What a notification says, its little icon, and where clicking it goes. */
function describe(n: InboxItem, partyOn: boolean): Described {
  switch (n.type) {
    case 'chat-message':
      return {
        text:
          n.attachment === 'gif'
            ? 'sent you a GIF'
            : n.attachment === 'image'
              ? 'sent you a picture'
              : 'sent you a message',
        detail: n.body || null,
        icon: <MessageCircle />,
        tone: 'bg-[#3b82f6] text-white',
        to: chatPath(n.from.id),
      };
    case 'recommendation':
      return {
        text: <>shared {bold(n.media.title)} with you</>,
        detail: n.message ? `“${n.message}”` : null,
        icon: <Film />,
        tone: 'bg-[#f59e0b] text-[#1c1206]',
        poster: n.media.posterUrl,
        to: titlePath(n.media.contentType, n.media.mediaId),
      };
    case 'watching':
      return {
        text: <>was watching {bold(n.media.title)}</>,
        detail: episodeOf(n.media),
        icon: <Eye />,
        tone: 'bg-[#8b5cf6] text-white',
        poster: n.media.posterUrl,
        to: titlePath(n.media.contentType, n.media.mediaId),
      };
    case 'friend-request':
      return {
        text: 'sent you a friend request',
        icon: <UserPlus />,
        tone: 'bg-[#ec4899] text-white',
        to: '/friends?tab=manage',
      };
    case 'friend-accepted':
      return {
        text: 'accepted your friend request',
        icon: <UserCheck />,
        tone: 'bg-[#22c55e] text-[#052e14]',
        to: '/friends',
      };
    case 'party-invite':
      return {
        text: 'invited you to a Party',
        icon: <PartyPopper />,
        tone: 'bg-[#f97316] text-white',
        to: partyOn ? `/party/${n.code}` : null,
      };
  }
}

/**
 * The bell in the header: chat messages, shared titles, friend requests and the rest, kept for when
 * you weren't looking. Opening it reads nothing by itself: you mark them as read, or open one.
 */
export function NotificationBell() {
  const can = useCanSocialise();
  const { data } = useInbox();
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const partyOn = usePartyEnabled();
  if (!can) return null;

  const unread = data?.unread ?? 0;
  const all = data?.items ?? [];
  const items = unreadOnly ? all.filter((i) => !i.readAt) : all;
  const run = (p: Promise<unknown>) => void p.catch((e) => toast.error(errorMessage(e)));

  return (
    <PO.Root open={open} onOpenChange={setOpen}>
      <PO.Trigger
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        data-tip="Notifications"
        className="relative grid h-10 w-10 place-items-center rounded-full text-text/75 outline-none transition-colors hover:bg-white/8 hover:text-text focus-visible:ring-2 focus-visible:ring-pink data-[state=open]:bg-white/8 data-[state=open]:text-text"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#e5484d] px-1 text-[11px] font-bold leading-none text-white shadow-md ring-2 ring-bg"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </PO.Trigger>
      <PO.Portal>
        <PO.Content
          align="end"
          sideOffset={10}
          collisionPadding={12}
          aria-label="Notifications"
          role="dialog"
          className="z-50 flex max-h-[min(560px,calc(100vh-96px))] w-[min(400px,calc(100vw-24px))] origin-top-right flex-col overflow-hidden rounded-2xl border border-line bg-bg-2/95 shadow-2xl shadow-black/60 backdrop-blur-xl data-[state=open]:animate-[dialog-in_200ms_var(--ease-out-soft)]"
        >
          <div className="flex items-center gap-2 border-b border-line px-4 pb-3 pt-3.5">
            <h2 className="text-lg font-semibold">Notifications</h2>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                disabled={!unread}
                onClick={() => run(markInboxRead(qc))}
                aria-label="Mark all as read"
                className="grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-white/8 hover:text-text disabled:pointer-events-none disabled:opacity-40"
              >
                <CheckCheck className="h-4.5 w-4.5" />
              </button>
              <button
                type="button"
                disabled={!all.length}
                onClick={() => run(clearInbox(qc))}
                aria-label="Clear all notifications"
                data-tip="Clear all"
                className="grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:pointer-events-none disabled:opacity-40"
              >
                <Trash2 className="h-4.5 w-4.5" />
              </button>
            </div>
          </div>
          <div role="tablist" aria-label="Show" className="flex gap-1 px-3 pt-2.5">
            {[
              { label: 'All', on: !unreadOnly, set: false },
              { label: unread ? `Unread (${unread})` : 'Unread', on: unreadOnly, set: true },
            ].map((t) => (
              <button
                key={t.set ? 'unread' : 'all'}
                type="button"
                role="tab"
                aria-selected={t.on}
                onClick={() => setUnreadOnly(t.set)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors',
                  t.on ? 'bg-pink/15 text-pink' : 'text-muted hover:bg-white/6 hover:text-text',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {items.length ? (
            <ul
              className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2.5"
              aria-label="Notifications list"
            >
              {items.map((n) => {
                const d = describe(n, partyOn);
                return (
                  <li key={n.id} className="group relative" data-unread={!n.readAt || undefined}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!n.readAt) run(markInboxRead(qc, [n.id]));
                        if (d.to) {
                          setOpen(false);
                          navigate(d.to);
                        }
                      }}
                      className={cn(
                        'flex w-full gap-3 rounded-xl border p-2.5 pr-16 text-left transition-colors hover:bg-white/6',
                        n.readAt ? 'border-transparent' : 'border-pink/15 bg-pink/[0.06]',
                      )}
                    >
                      <span className="relative shrink-0 self-start">
                        <ProfileAvatar
                          avatar={n.from.avatar}
                          image={n.from.avatarUrl}
                          size={40}
                          rounded="full"
                        />
                        <span
                          className={cn(
                            'absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full ring-2 ring-bg-2 [&>svg]:h-3 [&>svg]:w-3',
                            d.tone,
                          )}
                        >
                          {d.icon}
                        </span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-snug">
                          <span className="font-semibold">{n.from.name}</span> {d.text}
                        </span>
                        {d.detail && (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-muted">
                            {d.detail}
                          </span>
                        )}
                        <span
                          className={cn(
                            'mt-1 block text-[11px]',
                            n.readAt ? 'text-muted' : 'font-semibold text-pink',
                          )}
                        >
                          {ago(n.at)}
                        </span>
                      </span>
                      {d.poster && (
                        <img
                          loading="lazy"
                          decoding="async"
                          src={d.poster}
                          alt=""
                          className="h-14 w-10 shrink-0 rounded-md object-cover ring-1 ring-white/10"
                        />
                      )}
                    </button>
                    {!n.readAt && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute right-3 top-4 h-2 w-2 rounded-full bg-pink transition-opacity group-focus-within:opacity-0 group-hover:opacity-0"
                      />
                    )}
                    <div className="absolute right-2 top-2 flex gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
                      {!n.readAt && (
                        <button
                          type="button"
                          onClick={() => run(markInboxRead(qc, [n.id]))}
                          aria-label="Mark as read"
                          className="grid h-7 w-7 place-items-center rounded-full bg-bg-2/90 text-muted hover:bg-white/10 hover:text-text"
                        >
                          <CheckCheck className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => run(clearInbox(qc, [n.id]))}
                        aria-label="Clear notification"
                        data-tip="Clear"
                        className="grid h-7 w-7 place-items-center rounded-full bg-bg-2/90 text-muted hover:bg-white/10 hover:text-text"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="grid place-items-center gap-2 px-6 py-12 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-white/6 text-muted">
                <Bell className="h-5 w-5" />
              </span>
              <p className="font-semibold">
                {unreadOnly && all.length ? 'Nothing unread' : 'You’re all caught up'}
              </p>
              <p className="text-sm text-muted">
                Messages, shared titles and friend requests show up here.
              </p>
            </div>
          )}
        </PO.Content>
      </PO.Portal>
    </PO.Root>
  );
}
